import angular from 'angular';
import { Empresa, Estabelecimento } from '../../../../../../core/usuario/models/usuario-model';
import { ListagemService } from '../../../../../../shared/services/listagem.service';
import {
    IUploadDocumentoForm,
    IUploadDocumentoTrabalhadorSelecionado,
} from '../../models/upload-documentos-em-lote.form.model';
import {
    ISelecaoDeColaboradoresUiState,
    OrigemSincronizacaoEstabelecimentoType,
} from './models/selecao-de-colaboradores.form.model';

/**
 * Seleção de colaboradores em duas abas: por filtro ou por trabalhador.
 *
 * Mantém a sincronização bidirecional departamento/lotação -> estabelecimento e os
 * broadcasts de refresh dos selects dependentes.
 */
export class SelecaoDeColaboradoresFormController {

    static $inject = [
        '$scope',
        '$rootScope',
        'ListagemService',
    ];

    public form: angular.IFormController;
    public editable = false;
    public entity: IUploadDocumentoForm;

    public empresas: Empresa[] = [];
    public estabelecimentos: Estabelecimento[] = [];
    public uiState: ISelecaoDeColaboradoresUiState = this.createUiState();

    /**
     * Sem LIBERAR_REFATORACAO_VISIBILIDADE o usuário opera apenas a empresa e o
     * estabelecimento em que está logado: os dois selects ficam ocultos no template
     * e o escopo fica fixo no da sessão.
     */
    public visibilidadeLiberada = false;

    constructor(
        public $scope: angular.IScope,
        public $rootScope: angular.IRootScopeService & {
            session: any,
            nsjGlobals: any,
            liberadoTela: (arg: string) => boolean,
        },
        public ListagemService: ListagemService,
    ) { }

    $onInit() {
        this.visibilidadeLiberada = !!this.$rootScope.liberadoTela('LIBERAR_REFATORACAO_VISIBILIDADE');
        this.initializeEntity();
        this.initializeUiState();
        this.carregaEmpresas();
        this.carregaEstabelecimentos();
        // a empresa ja vem definida (sessao ou pre-selecao) e nenhum watcher
        // dispara por isso: o label e resolvido aqui.
        this.syncEmpresaSelecionadaUi();
        this.setupWatchers();
    }

    createUiState(): ISelecaoDeColaboradoresUiState {
        return {
            filtro: {
                departamentoSelecionado: null,
                lotacaoSelecionada: null,
            },
            trabalhador: {
                selecionado: null,
                lista: [],
            },
            sincronizacao: {
                origemEstabelecimento: null,
            },
        };
    }

    initializeEntity() {
        this.entity = this.entity || {} as IUploadDocumentoForm;
        this.entity.aba_selecao_colaboradores = this.entity.aba_selecao_colaboradores || 1;
        this.entity.trabalhadores = this.entity.trabalhadores || [];
        this.entity.empresa = this.entity.empresa || this.getEmpresaDaSessao();
        // com a flag ligada, vazio significa "todos os estabelecimentos da empresa".
        // com ela desligada, o escopo e o estabelecimento logado, nao "todos".
        this.entity.estabelecimento = this.visibilidadeLiberada
            ? (this.entity.estabelecimento || '')
            : this.getEstabelecimentoDaSessao();
        this.entity.departamento_colaboradores = this.entity.departamento_colaboradores || null;
        this.entity.lotacao_colaboradores = this.entity.lotacao_colaboradores || null;
        this.entity.empresa_selecionada_ui = this.entity.empresa_selecionada_ui || null;
        this.entity.estabelecimento_selecionado_ui = this.entity.estabelecimento_selecionado_ui || null;
        this.entity.departamento_colaboradores_selecionado_ui = this.entity.departamento_colaboradores_selecionado_ui || null;
        this.entity.lotacao_colaboradores_selecionada_ui = this.entity.lotacao_colaboradores_selecionada_ui || null;
        this.entity.trabalhador_selecionado_ui = this.entity.trabalhador_selecionado_ui || null;
        this.entity.trabalhadores_selecionados_ui = this.entity.trabalhadores_selecionados_ui || [];
    }

    initializeUiState() {
        this.uiState = this.createUiState();
        this.uiState.trabalhador.lista = angular.copy(this.entity.trabalhadores_selecionados_ui || []);
        this.uiState.trabalhador.selecionado = angular.copy(this.entity.trabalhador_selecionado_ui || null);
    }

    setupWatchers() {
        this.observeEmpresa();
        this.observeEstabelecimento();
        this.observeDepartamento();
        this.observeLotacao();
        this.observeTrabalhador();
    }

    // ---------- empresa e estabelecimento ----------

    carregaEmpresas() {
        this.empresas = this.ListagemService.montaEmpresas(
            this.$rootScope.nsjGlobals?.a?.grupoempresarial,
            []
        );
    }

    carregaEstabelecimentos() {
        this.estabelecimentos = this.entity.empresa
            ? this.ListagemService.montaEstabelecimentos(this.entity.empresa)
            : [];
    }

    observeEmpresa() {
        this.$scope.$watch(
            () => this.entity?.empresa,
            (newValue?: string, oldValue?: string) => {
                if (newValue === oldValue) {
                    return;
                }

                this.carregaEstabelecimentos();
                this.resetFiltroState();
                this.resetTrabalhadorState();
                this.syncAllPayloads();
                this.refreshAllDependentSelects();
            },
        );
    }

    observeEstabelecimento() {
        this.$scope.$watch(
            () => this.entity?.estabelecimento,
            (newValue?: string, oldValue?: string) => {
                if (newValue === oldValue) {
                    return;
                }

                this.handleEstabelecimentoChange();
            },
        );
    }

    handleEstabelecimentoChange() {
        this.resetDependentSelectionsFromEstabelecimentoChange(this.uiState.sincronizacao.origemEstabelecimento);
        this.uiState.sincronizacao.origemEstabelecimento = null;
        this.syncFiltroPayloadFromUi();
        this.refreshFiltroSelects();
        this.refreshTrabalhadorSelect();
    }

    resetDependentSelectionsFromEstabelecimentoChange(origem: OrigemSincronizacaoEstabelecimentoType) {
        if (origem === 'departamento') {
            this.uiState.filtro.lotacaoSelecionada = null;
            return;
        }

        if (origem === 'lotacao') {
            this.uiState.filtro.departamentoSelecionado = null;
            return;
        }

        this.uiState.filtro.departamentoSelecionado = null;
        this.uiState.filtro.lotacaoSelecionada = null;
    }

    // ---------- aba: seleção por filtro ----------

    buildEmpresaEstabelecimentoConstructors() {
        return {
            empresa: this.entity.empresa,
            estabelecimento: this.entity.estabelecimento,
        };
    }

    buildDepartamentoConstructors() {
        return this.buildEmpresaEstabelecimentoConstructors();
    }

    buildLotacaoConstructors() {
        return this.buildEmpresaEstabelecimentoConstructors();
    }

    isEstabelecimentoSelectDisabled() {
        return !this.estabelecimentos.length;
    }

    observeDepartamento() {
        this.$scope.$watch(
            () => this.uiState.filtro.departamentoSelecionado,
            () => {
                this.syncFiltroPayloadFromUi();
                this.syncEstabelecimentoFromDepartamento();
            },
            true,
        );
    }

    observeLotacao() {
        this.$scope.$watch(
            () => this.uiState.filtro.lotacaoSelecionada,
            () => {
                this.syncFiltroPayloadFromUi();
                this.syncEstabelecimentoFromLotacao();
            },
            true,
        );
    }

    syncEstabelecimentoFromDepartamento() {
        const estabelecimento = this.uiState.filtro.departamentoSelecionado?.estabelecimento;

        if (!estabelecimento || this.entity.estabelecimento === estabelecimento) {
            return;
        }

        this.uiState.sincronizacao.origemEstabelecimento = 'departamento';
        this.entity.estabelecimento = estabelecimento;
    }

    syncEstabelecimentoFromLotacao() {
        const estabelecimento = this.uiState.filtro.lotacaoSelecionada?.estabelecimento;

        if (!estabelecimento || this.entity.estabelecimento === estabelecimento) {
            return;
        }

        this.uiState.sincronizacao.origemEstabelecimento = 'lotacao';
        this.entity.estabelecimento = estabelecimento;
    }

    syncFiltroPayloadFromUi() {
        this.syncEmpresaSelecionadaUi();
        this.syncEstabelecimentoSelecionadoUi();
        this.entity.departamento_colaboradores = this.uiState.filtro.departamentoSelecionado?.departamento || null;
        this.entity.lotacao_colaboradores = this.uiState.filtro.lotacaoSelecionada?.lotacao || null;
        this.entity.departamento_colaboradores_selecionado_ui = this.uiState.filtro.departamentoSelecionado
            ? angular.copy(this.uiState.filtro.departamentoSelecionado)
            : null;
        this.entity.lotacao_colaboradores_selecionada_ui = this.uiState.filtro.lotacaoSelecionada
            ? angular.copy(this.uiState.filtro.lotacaoSelecionada)
            : null;
    }

    /**
     * Resolve o nome da empresa escolhida para a prévia exibi-lo, inclusive quando
     * o select está fora do DOM e a empresa vem da sessão.
     */
    syncEmpresaSelecionadaUi() {
        if (!this.entity.empresa) {
            this.entity.empresa_selecionada_ui = null;
            return;
        }

        const empresaSelecionada = this.empresas.find((empresa) => empresa.id === this.entity.empresa);

        this.entity.empresa_selecionada_ui = empresaSelecionada ? {
            empresa: empresaSelecionada.id,
            codigo: empresaSelecionada.codigo,
            razaosocial: empresaSelecionada.razaosocial,
        } : null;
    }

    syncEstabelecimentoSelecionadoUi() {
        if (!this.entity.estabelecimento) {
            this.entity.estabelecimento_selecionado_ui = null;
            return;
        }

        const estabelecimentoSelecionado = this.estabelecimentos.find((estabelecimento) => (
            estabelecimento.id === this.entity.estabelecimento
        ));

        this.entity.estabelecimento_selecionado_ui = estabelecimentoSelecionado ? {
            estabelecimento: estabelecimentoSelecionado.id,
            codigo: estabelecimentoSelecionado.codigo,
            nomefantasia: estabelecimentoSelecionado.nomefantasia,
        } : null;
    }

    refreshFiltroSelects() {
        this.$rootScope.$broadcast('departamentoColaboradoresUploadLote_refresh', this.buildDepartamentoConstructors());
        this.$rootScope.$broadcast('lotacaoColaboradoresUploadLote_refresh', this.buildLotacaoConstructors());
    }

    resetFiltroState() {
        // com a flag desligada o estabelecimento nao volta para "todos": ele e o
        // escopo da sessao, nao um filtro que o usuario possa limpar.
        this.entity.estabelecimento = this.visibilidadeLiberada ? '' : this.getEstabelecimentoDaSessao();
        this.entity.departamento_colaboradores = null;
        this.entity.lotacao_colaboradores = null;
        this.entity.estabelecimento_selecionado_ui = null;
        this.entity.departamento_colaboradores_selecionado_ui = null;
        this.entity.lotacao_colaboradores_selecionada_ui = null;
        this.uiState.filtro.departamentoSelecionado = null;
        this.uiState.filtro.lotacaoSelecionada = null;
        this.uiState.sincronizacao.origemEstabelecimento = null;
    }

    // ---------- aba: seleção por trabalhador ----------

    buildTrabalhadorConstructors() {
        return {
            status: '01',
            tipo_excluir: 1,
            empresa: this.entity.empresa,
            estabelecimento: this.entity.estabelecimento,
        };
    }

    isTrabalhadorSelectDisabled() {
        return !this.entity.empresa;
    }

    observeTrabalhador() {
        this.$scope.$watch(
            () => this.uiState.trabalhador.selecionado,
            () => {
                if (!this.uiState.trabalhador.selecionado) {
                    return;
                }

                this.addTrabalhador(this.uiState.trabalhador.selecionado);

                this.$scope.$evalAsync(() => {
                    this.uiState.trabalhador.selecionado = null;
                    this.entity.trabalhador_selecionado_ui = null;
                });
            },
            true,
        );
    }

    addTrabalhador(trabalhador: IUploadDocumentoTrabalhadorSelecionado) {
        const trabalhadorJaSelecionado = this.uiState.trabalhador.lista.some((item) => (
            item.trabalhador === trabalhador.trabalhador
        ));

        if (trabalhadorJaSelecionado) {
            return;
        }

        this.uiState.trabalhador.lista = [
            ...this.uiState.trabalhador.lista,
            {
                trabalhador: trabalhador.trabalhador,
                nome: trabalhador.nome,
                codigo: trabalhador.codigo,
            },
        ];

        this.syncTrabalhadoresPayloadFromUi();
    }

    onRemoveTrabalhador(trabalhadorRemovido: IUploadDocumentoTrabalhadorSelecionado) {
        this.uiState.trabalhador.lista = this.uiState.trabalhador.lista.filter((trabalhador) => (
            trabalhador.trabalhador !== trabalhadorRemovido.trabalhador
        ));

        this.syncTrabalhadoresPayloadFromUi();
    }

    syncTrabalhadoresPayloadFromUi() {
        this.entity.trabalhadores = this.uiState.trabalhador.lista.map((trabalhador) => ({
            trabalhador: trabalhador.trabalhador,
        }));
        this.entity.trabalhadores_selecionados_ui = angular.copy(this.uiState.trabalhador.lista);
        this.entity.trabalhador_selecionado_ui = angular.copy(this.uiState.trabalhador.selecionado);
    }

    refreshTrabalhadorSelect() {
        this.$rootScope.$broadcast('trabalhadorColaboradoresUploadLote_refresh', this.buildTrabalhadorConstructors());
    }

    resetTrabalhadorState() {
        this.uiState.trabalhador.selecionado = null;
        this.uiState.trabalhador.lista = [];
        this.entity.trabalhadores = [];
        this.entity.trabalhador_selecionado_ui = null;
        this.entity.trabalhadores_selecionados_ui = [];
    }

    // ---------- helpers ----------

    getEmpresaDaSessao(): string {
        return this.$rootScope.session?.entities?.empresa?.empresa || '';
    }

    getEstabelecimentoDaSessao(): string {
        return this.$rootScope.session?.entities?.estabelecimento?.estabelecimento || '';
    }

    syncAllPayloads() {
        this.syncFiltroPayloadFromUi();
        this.syncTrabalhadoresPayloadFromUi();
    }

    refreshAllDependentSelects() {
        this.refreshFiltroSelects();
        this.refreshTrabalhadorSelect();
    }
}
